import { isLmsWindow } from "@/lib/lms"
import { Toaster } from "@/components/ui/sonner"
import { toast } from "@/components/ui/use-toast"
import { mutationCache, queryCache } from "@/lib/react-query"
import { useEffect } from "react"
import {
  insightForViewErrorMessage,
  licenseLimitReachedMessage,
  noAccountsForUserMessage,
} from "@simulead/shared"
import { logout } from "@/lib/auth"
import { useTempStore } from "@/lib/store"

export function ToasterWrapper() {
  const avoidBottomCenterToasts = useTempStore(
    (state) => state.avoidBottomCenterToasts
  )

  useEffect(() => {
    function handleError(
      error: any,
      secondArg?: any,
      _context?: any,
      mutation?: any
    ) {
      // A caller can opt its errors out of the global destructive TOAST — and
      // ONLY the toast; the license / email-redirect / 401-logout branches below
      // still run — when it owns its own fallback/recovery (e.g. transcription
      // falls back Google→Whisper and surfaces its own friendly error only if
      // BOTH fail), via `meta.skipGlobalErrorToast`. Meta is on the mutation (4th
      // arg) for mutations and the query (2nd arg) for queries — read from the
      // correct one, never `variables`, so a request payload can't spoof it.
      const meta = mutation ? mutation.meta : secondArg?.meta
      const skipToast = Boolean(meta?.skipGlobalErrorToast)
      const message = error.body?.message

      if (message === noAccountsForUserMessage) {
        const emailRedirectEntries = [
          {
            emailContains: "@deloitte.com.au",
            redirectUrl: "https://forms.office.com/e/HdpgCg3amW",
          },
        ]
        const email = error.body?.email
        if (email) {
          const foundEmailRedirectEntry = emailRedirectEntries.find((entry) =>
            email.includes(entry.emailContains)
          )
          if (foundEmailRedirectEntry) {
            window.location.href = foundEmailRedirectEntry.redirectUrl
          }
        }
        return
      }

      // Floating-license org at capacity: dashboard.tsx owns this UX via the
      // LicenseLimitReached modal (keyed off this exact message). Skip the toast
      // AND the 401 logout below — the org being full must not tear down the
      // user's session/query cache. Do not delete this suppression again.
      if (message === licenseLimitReachedMessage) {
        return
      }

      // Filtered Learning Insights generation failed: LearningInsights.tsx owns
      // this UX with its own inline error + retry state, so stay silent here to
      // avoid alarming the admin twice. Do not delete this suppression again.
      if (message === insightForViewErrorMessage) {
        return
      }

      // ENG-1476: never log out from the LMS launch window; it has no login of
      // its own, and a logout would sign the learner out of Ambr everywhere
      if (error.status === 401 && !isLmsWindow()) {
        logout()
      }

      if (skipToast) return
      toast({
        title: message ?? "Unknown error",
        variant: "destructive",
      })
    }

    mutationCache.config.onError = handleError
    queryCache.config.onError = handleError
  }, [])

  // closeButton renders sonner's native per-toast X; bottom-center keeps
  // toasts clear of the sidebar and nav controls. A surface that owns the
  // bottom of the viewport (the presentation run puts the self-view and the
  // body language activation sweep there) flips them to the top instead, so a
  // toast never covers the presenter's own video.
  return (
    <Toaster
      richColors
      closeButton
      position={avoidBottomCenterToasts ? "top-center" : "bottom-center"}
    />
  )
}
